import React, { useRef, useState, useEffect } from "react";
import "../../App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import { useAuth } from "../../utils/AuthContext";
// Assets
import chessboardImg from "../../assets/ExtensionDarkNoName.webp";
import backgroundsquareHero from "../../assets/backgroundsquares1-Hero.svg";
import backgroundsquareDemo from "../../assets/backgroundsquares2-Demo.svg";
import backgroundsquareFooter from "../../assets/backgroundsquares5-Footer.svg";
import backgroundsquarePricingMobile from "../../assets/backgroundsquares3-Install.svg";
import pawnwhite from "../../assets/pawn-white.webp";
import knightwhite from "../../assets/knight-white.webp";
import rookwhite from "../../assets/rook-white.webp";
import backgroundsquarePricing from "../../assets/backgroundsquares4-Pricing.svg";
import logowhite from "../../assets/logowhitetransition.svg";
// Bits
import Words from "./bits/DescriptionSection";
import InstallSection from "./bits/InstallSection";
import FeatureSection from "./bits/FeatureSection";
import ReviewSection from "./bits/ReviewSection";
import PricingSection from "./bits/PricingSection";
import FAQSection from "./bits/FAQSection";
// Components
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";

function App() {
    const chessboardRef = useRef(null);
    const { getLicense, license, user, sendOnboardingEmail } = useAuth();

    useEffect(() => {
        if (license === "Loading..." && user) {
            getLicense();
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Check if the URL contains ?scrollTo=pricing-section and scroll if so
        const params = new URLSearchParams(window.location.search);
        const scrollToSection = params.get("scrollTo");
        if (scrollToSection) {
            setTimeout(() => {
                const section = document.getElementById(scrollToSection);
                if (section) {
                    section.scrollIntoView({ behavior: "smooth" });
                }
            }, 100); // slight delay to ensure DOM is ready
        }
    }, []);

    useEffect(() => {
        async function checkOAuthSuccess() {
            const urlParams = new URLSearchParams(window.location.search);
            const oauthSuccess = urlParams.get("oauth_success");

            if (oauthSuccess === "google") {
                try {
                    // Remove the parameter from URL without refreshing
                    window.history.replaceState(
                        {},
                        "",
                        window.location.pathname
                    );

                    // Call the send onboarding email function
                    await sendOnboardingEmail();
                } catch (err) {
                    console.error("Google login check failed:", err);
                }
            }
        }

        checkOAuthSuccess();
    }, [sendOnboardingEmail]);

    // 1) Board-tilt scroll progress (ends early, at 32%)
    const { scrollYProgress: boardScroll } = useScroll({
        target: chessboardRef,
        offset: ["start end", "start 32%"],
    });

    // 2) Pieces scroll progress (runs all the way until bottom hits top)
    const { scrollYProgress: piecesScroll } = useScroll({
        target: chessboardRef,
        offset: ["start end", "end start"],
    });

    const tilt = useTransform(boardScroll, [0, 1], [24, 0]);

    const pawnLeftY = useTransform(piecesScroll, [0, 1], ["200px", "0px"]);

    // knight will start moving later and finish on time (slow start)
    const knightY = useTransform(piecesScroll, [0.4, 1], ["300px", "0px"]);

    // rook will ease in and out (mid-speed)
    const rookY = useTransform(
        piecesScroll,
        [0, 0.2, 1],
        ["250px", "75px", "0px"]
    );

    // pawnRight stays down longer, then zooms up at the end
    const pawnRightY = useTransform(
        piecesScroll,
        [0, 0.3, 1], // <— holds around 250px until 80%, then snaps up
        ["250px", "250px", "0px"]
    );

    const pawnLeftR = useTransform(piecesScroll, [0, 1], ["-5deg", "0deg"]);
    const knightR = useTransform(piecesScroll, [0, 1], ["5deg", "0deg"]);
    const rookR = useTransform(piecesScroll, [0, 1], ["-8deg", "0deg"]);
    const pawnRightR = useTransform(piecesScroll, [0, 1], ["6deg", "0deg"]);

    const [hoverStyle, setHoverStyle] = useState({});

    const handleMouseMove = (e) => {
        const button = e.currentTarget;
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left; // Mouse X relative to the button

        // Width of the ::after element (50% of the button width)
        const afterWidth = rect.width * 0.82;

        // Clamp the X position to stay within the button's bounds, accounting for the ::after width
        const clampedX = Math.max(
            afterWidth / 2,
            Math.min(x, rect.width - afterWidth / 2)
        );

        setHoverStyle({
            "--hover-x": `${clampedX}px`,
        });
    };

    const handleMouseLeave = () => {
        setHoverStyle({});
    };

    // const scrollTo = (sectionId) => {
    //     const section = document.querySelector(sectionId);
    //     if (section) {
    //         section.scrollIntoView({ behavior: "smooth" });
    //     }
    // };

    return (
        <>
            <title>
                Oakmate | Free Chess Game Review Extension for Chess.com &
                Lichess
            </title>
            <meta
                name="description"
                content="Oakmate is a free Chrome extension for fast, in-depth chess game review. Instantly spot mistakes, estimate Elo, and train with custom puzzles."
            />
            <div className="landing">
                <Navbar isDownloadVisible={true} />
                <main className="landing-main">
                    <div className="Sections-Container">
                        <section className="heroSection-Container">
                            <img
                                src={backgroundsquareHero}
                                alt="Background hero"
                                className="backgroundsquareHero"
                                title="Background Hero Image"
                            />
                            <div className="herocopy-container">
                                <h1 className="hero-title">
                                    Own Your <br />
                                    Game Review
                                </h1>
                                <p className="hero-subtitle-mobile">
                                    Get pro-grade analysis for free with a
                                    convenient popup overlay right on your game
                                    page.
                                </p>
                                <p className="hero-subtitle">
                                    Get pro-grade analysis for free with a
                                    convenient
                                    <br /> popup overlay right on your game
                                    page.
                                </p>
                                <div className="cta-wrapper">
                                    <a
                                        className="download-cta"
                                        style={hoverStyle}
                                        onMouseMove={handleMouseMove}
                                        onMouseLeave={handleMouseLeave}
                                        href="https://chromewebstore.google.com/detail/oakmate-game-review/fdledmdlgggnfddaeillbpahblnblbne"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span className="download-cta-text">
                                            Add To Chrome - It's free
                                        </span>
                                        {/* <img
                                            src={DownloadIcon}
                                            className="download-cta-icon"
                                            alt="Download Icon"
                                            title="Download Icon"
                                        /> */}
                                        <svg
                                            width="24"
                                            height="24"
                                            fill="none"
                                            viewBox="0 0 25 24"
                                            className="download-cta-icon"
                                        >
                                            <g>
                                                <path
                                                    fill="#E94737"
                                                    d="M12.405 1.969a9.935 9.935 0 0 0-9.936 9.936 9.935 9.935 0 0 0 9.936 9.936 9.935 9.935 0 0 0 9.936-9.936 9.935 9.935 0 0 0-9.936-9.936Z"
                                                ></path>
                                                <path
                                                    fill="#30A04C"
                                                    d="m3.697 16.693.198.346a9.927 9.927 0 0 0 8.51 4.802c.09 0 .18 0 .272-.002l-.158-.277L16.9 19.95V14.27H8.308l-4.37-7.568a9.89 9.89 0 0 0-1.47 5.202c0 1.737.446 3.369 1.229 4.788Z"
                                                ></path>
                                                <path
                                                    fill="#FBBC1C"
                                                    d="M21.158 7.2v-.028h-.014a9.307 9.307 0 0 0-.272-.47l-.272.47h-8.195v7.098h4.098l-4.37 7.57a9.937 9.937 0 0 0 8.876-4.968 9.918 9.918 0 0 0 .15-9.671Z"
                                                ></path>
                                                <path
                                                    fill="#E94737"
                                                    d="M21.01 6.937a9.931 9.931 0 0 0-8.434-4.968h-.357A9.87 9.87 0 0 0 7.438 3.3a9.88 9.88 0 0 0-3.504 3.406l.27.466h16.938a11.704 11.704 0 0 0-.132-.236Z"
                                                ></path>
                                                <path
                                                    fill="#FEFEFD"
                                                    d="M12.405 7.173a4.73 4.73 0 0 0-4.732 4.732 4.73 4.73 0 0 0 4.732 4.732 4.73 4.73 0 0 0 4.732-4.732 4.73 4.73 0 0 0-4.732-4.732Z"
                                                ></path>
                                                <path
                                                    fill="#3884F0"
                                                    d="M12.405 8.12a3.784 3.784 0 0 0-3.785 3.785 3.784 3.784 0 0 0 3.785 3.785 3.784 3.784 0 0 0 3.785-3.785 3.784 3.784 0 0 0-3.785-3.785Z"
                                                ></path>
                                            </g>
                                        </svg>
                                    </a>
                                    <a
                                        className="download-cta-mobile"
                                        href="https://chromewebstore.google.com/detail/oakmate-game-review/fdledmdlgggnfddaeillbpahblnblbne"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span className="download-cta-text">
                                            Add To Chrome - It's free
                                        </span>
                                        <svg
                                            width="24"
                                            height="24"
                                            fill="none"
                                            viewBox="0 0 25 24"
                                            className="download-cta-icon"
                                        >
                                            <g>
                                                <path
                                                    fill="#E94737"
                                                    d="M12.405 1.969a9.935 9.935 0 0 0-9.936 9.936 9.935 9.935 0 0 0 9.936 9.936 9.935 9.935 0 0 0 9.936-9.936 9.935 9.935 0 0 0-9.936-9.936Z"
                                                ></path>
                                                <path
                                                    fill="#30A04C"
                                                    d="m3.697 16.693.198.346a9.927 9.927 0 0 0 8.51 4.802c.09 0 .18 0 .272-.002l-.158-.277L16.9 19.95V14.27H8.308l-4.37-7.568a9.89 9.89 0 0 0-1.47 5.202c0 1.737.446 3.369 1.229 4.788Z"
                                                ></path>
                                                <path
                                                    fill="#FBBC1C"
                                                    d="M21.158 7.2v-.028h-.014a9.307 9.307 0 0 0-.272-.47l-.272.47h-8.195v7.098h4.098l-4.37 7.57a9.937 9.937 0 0 0 8.876-4.968 9.918 9.918 0 0 0 .15-9.671Z"
                                                ></path>
                                                <path
                                                    fill="#E94737"
                                                    d="M21.01 6.937a9.931 9.931 0 0 0-8.434-4.968h-.357A9.87 9.87 0 0 0 7.438 3.3a9.88 9.88 0 0 0-3.504 3.406l.27.466h16.938a11.704 11.704 0 0 0-.132-.236Z"
                                                ></path>
                                                <path
                                                    fill="#FEFEFD"
                                                    d="M12.405 7.173a4.73 4.73 0 0 0-4.732 4.732 4.73 4.73 0 0 0 4.732 4.732 4.73 4.73 0 0 0 4.732-4.732 4.73 4.73 0 0 0-4.732-4.732Z"
                                                ></path>
                                                <path
                                                    fill="#3884F0"
                                                    d="M12.405 8.12a3.784 3.784 0 0 0-3.785 3.785 3.784 3.784 0 0 0 3.785 3.785 3.784 3.784 0 0 0 3.785-3.785 3.784 3.784 0 0 0-3.785-3.785Z"
                                                ></path>
                                            </g>
                                        </svg>
                                    </a>
                                    {/* <a
                                        className="seeprice-cta"
                                        href="#pricing-section"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            scrollTo("#pricing-section");
                                        }}
                                    >
                                        <span className="seeprice-cta-text">
                                            See Pricing
                                        </span>
                                    </a> */}
                                </div>
                                <div className="socialproof-wrapper">
                                    <span className="socialproof-text">
                                        ✓ 1208 puzzles completed
                                    </span>
                                    <span className="socialproof-text">
                                        ✓ 3021 games analyzed
                                    </span>
                                </div>
                            </div>
                        </section>
                        <section className="demoSection-Container-desktop">
                            <div
                                className="chessboardcontainer"
                                ref={chessboardRef}
                            >
                                <img
                                    src={backgroundsquareDemo}
                                    alt="Background"
                                    className="backgroundsquareDemo"
                                />
                                <motion.img
                                    className="chessboard"
                                    style={{
                                        rotateX: tilt,
                                        transformStyle: "preserve-3d",
                                    }}
                                    src={chessboardImg}
                                    alt="Product Demo"
                                    title="Oakmate Extension Demo Game Review"
                                />
                                <motion.img
                                    className="pawnwhite-left"
                                    src={pawnwhite}
                                    alt="pawn left"
                                    style={{
                                        "--ty": pawnLeftY,
                                        "--r": pawnLeftR,
                                    }}
                                    title="White pawn left"
                                />
                                {/* knight */}
                                <motion.img
                                    className="knightwhite"
                                    src={knightwhite}
                                    alt="knight"
                                    style={{
                                        "--ty": knightY,
                                        "--r": knightR,
                                    }}
                                    title="White knight"
                                />
                                {/* rook */}
                                <motion.img
                                    className="rookwhite"
                                    src={rookwhite}
                                    alt="rook"
                                    style={{
                                        "--ty": rookY,
                                        "--r": rookR,
                                    }}
                                    title="White rook"
                                ></motion.img>
                                {/* pawn right */}
                                <motion.img
                                    className="pawnwhite-right"
                                    src={pawnwhite}
                                    alt="pawn right"
                                    style={{
                                        "--ty": pawnRightY,
                                        "--r": pawnRightR,
                                    }}
                                    title="White pawn right"
                                />
                            </div>
                        </section>
                        <section className="demoSection-Container-mobile">
                            <div className="mobileDemo-wrapper">
                                <h2 className="mobileDemo-title">
                                    Built on what works...
                                </h2>
                                <div className="mobileDemo-img-wrapper">
                                    <motion.img
                                        src={chessboardImg}
                                        alt="chessboard"
                                        className="mobileDemo-img-rightPanel"
                                        initial={{ x: "-60%" }}
                                        whileInView={{ x: "-27%" }}
                                        transition={{
                                            type: "spring",
                                            damping: 20,
                                            stiffness: 120,
                                            mass: 0.5,
                                        }}
                                        viewport={{ once: true }}
                                    />
                                </div>
                            </div>
                            <div className="mobileDemo-wrapper">
                                <motion.h2
                                    className="mobileDemo-title"
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.7,
                                        ease: "easeOut",
                                        delay: 0.2,
                                    }}
                                    viewport={{ once: true }}
                                >
                                    ...upgraded with what's{" "}
                                    <span className="M2Gradient">missing</span>
                                </motion.h2>
                                <div className="mobileDemo-img-wrapper">
                                    <img
                                        src={backgroundsquareHero}
                                        alt="Background Demo"
                                        className="backgroundsquareDemoMobile"
                                        title="Background Demo image"
                                    />
                                    <motion.img
                                        src={chessboardImg}
                                        alt="chessboard"
                                        className="mobileDemo-img-leftPanel"
                                        initial={{ x: "60%" }}
                                        whileInView={{ x: "27%" }}
                                        transition={{
                                            type: "spring",
                                            damping: 20,
                                            stiffness: 120,
                                            mass: 0.5,
                                        }}
                                        viewport={{ once: true }}
                                        title="Chessboard demo left panel"
                                    />
                                </div>
                            </div>
                        </section>
                        <Words />
                        <section className="featureSection">
                            <div className="logowhite-wrapper">
                                <img
                                    src={logowhite}
                                    alt="Logo white"
                                    className="logowhite"
                                    title="Oakmate logo white"
                                />
                            </div>
                            <div className="featureSection-Container">
                                <FeatureSection />
                            </div>
                        </section>
                        <section className="installSection-Container">
                            <InstallSection />
                        </section>
                        <section className="reviewSection-Container">
                            <ReviewSection />
                        </section>
                        <section className="pricingSection">
                            <img
                                src={backgroundsquarePricing}
                                alt="Background"
                                className="backgroundsquarePricing"
                                title="Pricing section background"
                            />
                            <div
                                className="pricingSection-Container"
                                id="pricing-section"
                            >
                                <PricingSection />
                            </div>
                            <div className="backgroundsquarePricing-wrapper-mobile">
                                <img
                                    src={backgroundsquarePricingMobile}
                                    alt="Background"
                                    className="backgroundsquarePricing-mobile"
                                    title="Pricing section background mobile"
                                />
                            </div>
                        </section>
                        <section className="FAQSection-Container">
                            <FAQSection />
                        </section>
                        <section className="LastCTASection-Container">
                            <h2 className="cta-title">Try Oakmate Now</h2>
                            <a
                                className="download-cta"
                                href="https://chromewebstore.google.com/detail/oakmate-game-review/fdledmdlgggnfddaeillbpahblnblbne"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className="download-cta-text">
                                    Add To Chrome - It's free
                                </span>
                                <svg
                                    width="24"
                                    height="24"
                                    fill="none"
                                    viewBox="0 0 25 24"
                                    className="download-cta-icon"
                                >
                                    <g>
                                        <path
                                            fill="#E94737"
                                            d="M12.405 1.969a9.935 9.935 0 0 0-9.936 9.936 9.935 9.935 0 0 0 9.936 9.936 9.935 9.935 0 0 0 9.936-9.936 9.935 9.935 0 0 0-9.936-9.936Z"
                                        ></path>
                                        <path
                                            fill="#30A04C"
                                            d="m3.697 16.693.198.346a9.927 9.927 0 0 0 8.51 4.802c.09 0 .18 0 .272-.002l-.158-.277L16.9 19.95V14.27H8.308l-4.37-7.568a9.89 9.89 0 0 0-1.47 5.202c0 1.737.446 3.369 1.229 4.788Z"
                                        ></path>
                                        <path
                                            fill="#FBBC1C"
                                            d="M21.158 7.2v-.028h-.014a9.307 9.307 0 0 0-.272-.47l-.272.47h-8.195v7.098h4.098l-4.37 7.57a9.937 9.937 0 0 0 8.876-4.968 9.918 9.918 0 0 0 .15-9.671Z"
                                        ></path>
                                        <path
                                            fill="#E94737"
                                            d="M21.01 6.937a9.931 9.931 0 0 0-8.434-4.968h-.357A9.87 9.87 0 0 0 7.438 3.3a9.88 9.88 0 0 0-3.504 3.406l.27.466h16.938a11.704 11.704 0 0 0-.132-.236Z"
                                        ></path>
                                        <path
                                            fill="#FEFEFD"
                                            d="M12.405 7.173a4.73 4.73 0 0 0-4.732 4.732 4.73 4.73 0 0 0 4.732 4.732 4.73 4.73 0 0 0 4.732-4.732 4.73 4.73 0 0 0-4.732-4.732Z"
                                        ></path>
                                        <path
                                            fill="#3884F0"
                                            d="M12.405 8.12a3.784 3.784 0 0 0-3.785 3.785 3.784 3.784 0 0 0 3.785 3.785 3.784 3.784 0 0 0 3.785-3.785 3.784 3.784 0 0 0-3.785-3.785Z"
                                        ></path>
                                    </g>
                                </svg>
                            </a>
                            <img
                                src={backgroundsquareFooter}
                                alt="Background"
                                className="backgroundsquareFooter"
                                title="Footer background"
                            />
                        </section>
                        <footer className="FooterSection-Container">
                            <Footer />
                        </footer>
                        <div className="copywrite">
                            ©2025 Oakmate. All rights reserved.
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}

export default App;
